import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function getHeaders() {
  const token = localStorage.getItem('auth_token');
  const h = { 'Content-Type': 'application/json' };
  if (token) h.Authorization = `Bearer ${token}`;
  return h;
}

const opts = () => ({ withCredentials: true, headers: getHeaders() });

// For multipart/form-data uploads. We MUST omit Content-Type so axios can
// auto-generate `multipart/form-data; boundary=...` from the FormData payload.
const uploadOpts = () => {
  const token = localStorage.getItem('auth_token');
  const headers = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  return { withCredentials: true, headers };
};

// Goals
export const fetchGoals = () => axios.get(`${API}/goals`, opts()).then(r => r.data);
export const fetchGoalsRadar = () => axios.get(`${API}/goals/radar`, opts()).then(r => r.data);
export const createGoal = (data) => axios.post(`${API}/goals`, data, opts()).then(r => r.data);
export const updateGoal = (id, data) => axios.put(`${API}/goals/${id}`, data, opts()).then(r => r.data);
export const deleteGoal = (id) => axios.delete(`${API}/goals/${id}`, opts()).then(r => r.data);

// Smart-scale / Body Composition
export const fetchScaleSummary = () => axios.get(`${API}/scale/measurements/summary`, opts()).then(r => r.data);
export const fetchScaleMeasurements = (limit = 200) => axios.get(`${API}/scale/measurements?limit=${limit}`, opts()).then(r => r.data);
export const createScaleMeasurement = (data) => axios.post(`${API}/scale/measurements`, data, opts()).then(r => r.data);
export const deleteScaleMeasurement = (id) => axios.delete(`${API}/scale/measurements/${id}`, opts()).then(r => r.data);
export const importScaleMeasurements = (measurements) => axios.post(`${API}/scale/measurements/import`, { measurements }, opts()).then(r => r.data);

// Group Chat
export const fetchGroupChatConsent = () => axios.get(`${API}/group-chat/consent`, opts()).then(r => r.data);
export const fetchGroupChatRooms = () => axios.get(`${API}/group-chat/rooms`, opts()).then(r => r.data);
export const fetchGroupChatPublicHighlights = () => axios.get(`${API}/group-chat/public/highlights`).then(r => r.data);
export const fetchGroupChatInvitePrefs = () => axios.get(`${API}/group-chat/invite-prefs`, opts()).then(r => r.data);
export const updateGroupChatInvitePrefs = (allow_group_invites) => axios.put(`${API}/group-chat/invite-prefs`, { allow_group_invites }, opts()).then(r => r.data);
export const fetchGroupChatVisibleMembers = (q = '') => axios.get(`${API}/group-chat/visible-members?q=${encodeURIComponent(q)}`, opts()).then(r => r.data);
export const createGroupChatRoom = (data) => axios.post(`${API}/group-chat/rooms`, data, opts()).then(r => r.data);
export const fetchGroupChatRoom = (id) => axios.get(`${API}/group-chat/rooms/${id}`, opts()).then(r => r.data);
export const joinGroupChatRoom = (id, data) => axios.post(`${API}/group-chat/rooms/${id}/join`, data, opts()).then(r => r.data);
export const leaveGroupChatRoom = (id) => axios.post(`${API}/group-chat/rooms/${id}/leave`, {}, opts()).then(r => r.data);
export const fetchGroupChatMessages = (id, limit = 200) => axios.get(`${API}/group-chat/rooms/${id}/messages?limit=${limit}`, opts()).then(r => r.data);
export const postGroupChatMessage = (id, content) => axios.post(`${API}/group-chat/rooms/${id}/messages`, { content }, opts()).then(r => r.data);
export const inviteToGroupChat = (id, email) => axios.post(`${API}/group-chat/rooms/${id}/invite`, { email }, opts()).then(r => r.data);
export const fetchGroupChatWeeklyReport = () => axios.get(`${API}/admin/group-chat/weekly-report`, opts()).then(r => r.data);
export const runGroupChatWeeklyReport = (emailAdmins = false) => axios.post(`${API}/admin/group-chat/weekly-report/run?email_admins=${emailAdmins}`, {}, opts()).then(r => r.data);

// Stripe payments
export const fetchPaymentPackages = () => axios.get(`${API}/payments/v1/packages`, opts()).then(r => r.data);
export const createPaymentCheckout = (data) => axios.post(`${API}/payments/v1/checkout/session`, data, opts()).then(r => r.data);
export const fetchPaymentStatus = (sessionId) => axios.get(`${API}/payments/v1/checkout/status/${sessionId}`, opts()).then(r => r.data);

// Chat
export const sendChatMessage = (message, sessionId = 'default') =>
  axios.post(`${API}/chat/send`, { message, session_id: sessionId }, opts()).then(r => r.data);
export const fetchChatHistory = (sessionId = 'default') =>
  axios.get(`${API}/chat/history?session_id=${sessionId}`, opts()).then(r => r.data);
export const generatePlan = (topic, sessionId = 'default') =>
  axios.post(`${API}/chat/generate-plan`, { topic, session_id: sessionId }, opts()).then(r => r.data);
export const fetchPlans = () => axios.get(`${API}/plans`, opts()).then(r => r.data);
export const fetchPlan = (planId) => axios.get(`${API}/plans/${planId}`, opts()).then(r => r.data);
export const deletePlan = (planId) => axios.delete(`${API}/plans/${planId}`, opts()).then(r => r.data);

// Store
export const fetchProducts = (category) =>
  axios.get(`${API}/store/products${category ? `?category=${category}` : ''}`, opts()).then(r => r.data);
export const fetchCart = () => axios.get(`${API}/cart`, opts()).then(r => r.data);
export const addToCart = (productId, quantity = 1) =>
  axios.post(`${API}/cart`, { product_id: productId, quantity }, opts()).then(r => r.data);
export const removeFromCart = (cartItemId) => axios.delete(`${API}/cart/${cartItemId}`, opts()).then(r => r.data);
export const checkoutCart = () => axios.post(`${API}/cart/checkout`, {}, opts()).then(r => r.data);

// Profile
export const fetchProfile = () => axios.get(`${API}/profile`, opts()).then(r => r.data);
export const updateProfile = (data) => axios.put(`${API}/profile`, data, opts()).then(r => r.data);
export const updatePreferences = (prefs) =>
  axios.put(`${API}/profile/preferences`, { preferences: prefs }, opts()).then(r => r.data);

// Scores
export const fetchScores = () => axios.get(`${API}/scores`, opts()).then(r => r.data);

// Wearables
export const fetchWearableDevices = () => axios.get(`${API}/wearables/devices`, opts()).then(r => r.data);
export const connectWearable = (deviceName, deviceType = 'smartwatch') =>
  axios.post(`${API}/wearables/connect`, { device_name: deviceName, device_type: deviceType }, opts()).then(r => r.data);
export const disconnectWearable = (deviceId) =>
  axios.delete(`${API}/wearables/devices/${deviceId}`, opts()).then(r => r.data);
export const syncWearableData = () => axios.post(`${API}/wearables/sync`, {}, opts()).then(r => r.data);
export const fetchWearableData = (metric, days = 7) =>
  axios.get(`${API}/wearables/data?${metric ? `metric=${metric}&` : ''}days=${days}`, opts()).then(r => r.data);
export const fetchWearableSummary = () => axios.get(`${API}/wearables/summary`, opts()).then(r => r.data);

// Notifications
export const sendTestNotification = (email, subject) =>
  axios.post(`${API}/notifications/test`, { email, subject }, opts()).then(r => r.data);
export const sendWeeklyDigest = () => axios.post(`${API}/notifications/send-digest`, {}, opts()).then(r => r.data);
export const sendScoreChangeNotification = () => axios.post(`${API}/notifications/score-change`, {}, opts()).then(r => r.data);
export const sendGoalReminder = () => axios.post(`${API}/notifications/goal-reminder`, {}, opts()).then(r => r.data);
export const fetchNotificationHistory = () => axios.get(`${API}/notifications/history`, opts()).then(r => r.data);
export const fetchNotificationStatus = () => axios.get(`${API}/notifications/status`, opts()).then(r => r.data);

// Survey
export const submitSurvey = (data) => axios.post(`${API}/survey`, data, opts()).then(r => r.data);
export const fetchSurvey = () => axios.get(`${API}/survey`, opts()).then(r => r.data);

// Admin
export const fetchAdminStats = () => axios.get(`${API}/admin/stats`, opts()).then(r => r.data);
export const fetchAdminUsers = (page = 1) => axios.get(`${API}/admin/users?page=${page}`, opts()).then(r => r.data);
export const updateAdminUser = (userId, data) => axios.put(`${API}/admin/users/${userId}`, data, opts()).then(r => r.data);
export const deleteAdminUser = (userId) => axios.delete(`${API}/admin/users/${userId}`, opts()).then(r => r.data);
export const fetchAdminOrders = (page = 1) => axios.get(`${API}/admin/orders?page=${page}`, opts()).then(r => r.data);
export const fetchAdminProducts = () => axios.get(`${API}/admin/products`, opts()).then(r => r.data);
export const addAdminProduct = (data) => axios.post(`${API}/admin/products`, data, opts()).then(r => r.data);
export const deleteAdminProduct = (productId) => axios.delete(`${API}/admin/products/${productId}`, opts()).then(r => r.data);

// Admin External Integrations — URLs + encrypted API secrets
export const fetchAdminIntegrations = () =>
  axios.get(`${API}/admin/integrations`, opts()).then(r => r.data);
export const updateAdminIntegration = (provider, data) =>
  axios.put(`${API}/admin/integrations/${provider}`, data, opts()).then(r => r.data);
export const testAdminIntegration = (provider) =>
  axios.post(`${API}/admin/integrations/${provider}/test`, {}, opts()).then(r => r.data);

// Admin Affiliates (partner / Amazon affiliate links shown in the Store)
export const fetchAdminAffiliates = () => axios.get(`${API}/admin/affiliates`, opts()).then(r => r.data);
export const addAdminAffiliate = (data) => axios.post(`${API}/admin/affiliates`, data, opts()).then(r => r.data);
export const updateAdminAffiliate = (productId, data) => axios.put(`${API}/admin/affiliates/${productId}`, data, opts()).then(r => r.data);
export const deleteAdminAffiliate = (productId) => axios.delete(`${API}/admin/affiliates/${productId}`, opts()).then(r => r.data);

// Admin Content Blasts
export const sendContentBlast = (data) => axios.post(`${API}/admin/content-blast`, data, opts()).then(r => r.data);
export const fetchContentBlasts = () => axios.get(`${API}/admin/content-blasts`, opts()).then(r => r.data);
export const fetchBlastSchedule = () => axios.get(`${API}/admin/blast-schedule`, opts()).then(r => r.data);
export const updateBlastSchedule = (data) => axios.post(`${API}/admin/blast-schedule`, data, opts()).then(r => r.data);
export const testAutoBlast = () => axios.post(`${API}/admin/blast-schedule/test`, {}, opts()).then(r => r.data);

// Research & Blog
export const fetchResearch = () => axios.get(`${API}/research`, opts()).then(r => r.data);
export const fetchBlogPosts = () => axios.get(`${API}/blog`, opts()).then(r => r.data);

// Admin Research
export const fetchAdminResearch = () => axios.get(`${API}/admin/research`, opts()).then(r => r.data);
export const createAdminResearch = (data) => axios.post(`${API}/admin/research`, data, opts()).then(r => r.data);
export const updateAdminResearch = (articleId, data) => axios.put(`${API}/admin/research/${articleId}`, data, opts()).then(r => r.data);
export const deleteAdminResearch = (articleId) => axios.delete(`${API}/admin/research/${articleId}`, opts()).then(r => r.data);
export const importAdminResearch = (data) => axios.post(`${API}/admin/research/import`, data, opts()).then(r => r.data);
export const scrapeAdminResearch = (data) => axios.post(`${API}/admin/research/scrape`, data, opts()).then(r => r.data);
export const uploadAdminResearch = (formData) => {
  const token = localStorage.getItem('auth_token');
  return axios.post(`${API}/admin/research/upload`, formData, {
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
    withCredentials: true,
  }).then(r => r.data);
};

// Challenges
export const fetchChallenges = () => axios.get(`${API}/challenges`, opts()).then(r => r.data);
export const fetchChallenge = (id) => axios.get(`${API}/challenges/${id}`, opts()).then(r => r.data);
export const joinChallenge = (id) => axios.post(`${API}/challenges/${id}/join`, {}, opts()).then(r => r.data);
export const logChallengeProgress = (id, value) => axios.post(`${API}/challenges/${id}/progress`, { value }, opts()).then(r => r.data);

// Membership
export const fetchMembershipTiers = () => axios.get(`${API}/membership/tiers`, opts()).then(r => r.data);
export const fetchSubscription = () => axios.get(`${API}/membership/subscription`, opts()).then(r => r.data);

// Orders
export const fetchUserOrders = () => axios.get(`${API}/orders`, opts()).then(r => r.data);

// Library (tier-gated)
export const fetchLibrary = () => axios.get(`${API}/library`, opts()).then(r => r.data);

// Seminars
export const fetchSeminarQuestionnaire = () => axios.post(`${API}/seminars/questionnaire`, {}, opts()).then(r => r.data);
export const generateSeminar = (data) => axios.post(`${API}/seminars/generate`, data, opts()).then(r => r.data);
export const fetchSeminars = (category = '', search = '') => {
  const params = new URLSearchParams();
  if (category) params.append('category', category);
  if (search) params.append('search', search);
  return axios.get(`${API}/seminars?${params}`, opts()).then(r => r.data);
};
export const fetchSeminar = (id) => axios.get(`${API}/seminars/${id}`, opts()).then(r => r.data);
export const deleteSeminar = (id) => axios.delete(`${API}/seminars/${id}`, opts()).then(r => r.data);
export const synthesizeSpeech = (text, voice = 'nova', model = 'tts-1') =>
  axios.post(`${API}/seminars/tts`, { text, voice, model }, opts()).then(r => r.data);

// AI Help
export const sendHelpMessage = (message, sessionId = 'help') =>
  axios.post(`${API}/help/chat`, { message, session_id: sessionId }, opts()).then(r => r.data);

// Tour
export const completeTour = () => axios.post(`${API}/tour/complete`, {}, opts()).then(r => r.data);

// Changelog
export const fetchChangelog = () => axios.get(`${API}/changelog`, opts()).then(r => r.data);
export const dismissChangelog = () => axios.post(`${API}/changelog/dismiss`, {}, opts()).then(r => r.data);


// ── AI token-rate ticker ──
export const fetchAiRates = () => axios.get(`${API}/ai-rates`).then(r => r.data);
export const updateAiRates = (rates) => axios.put(`${API}/admin/ai-rates`, { rates }, opts()).then(r => r.data);

// ── PHI / Medical Records ──
export const fetchPhiConsent = () =>
  axios.get(`${API}/phi/consent`, opts()).then(r => r.data);

export const submitPhiConsent = (policyVersion) =>
  axios.post(`${API}/phi/consent`, { agree: true, policy_version: policyVersion }, opts()).then(r => r.data);

export const fetchPhiTemplates = () =>
  axios.get(`${API}/phi/templates`, opts()).then(r => r.data);

export const downloadPhiTemplate = async (templateId, filename) => {
  const res = await axios.get(`${API}/phi/templates/${templateId}`, { ...opts(), responseType: 'blob' });
  const url = window.URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || `${templateId}.txt`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

export const listPhiRecords = (params = {}) =>
  axios.get(`${API}/phi/records`, { ...opts(), params }).then(r => r.data);

export const getPhiRecord = (id) =>
  axios.get(`${API}/phi/records/${id}`, opts()).then(r => r.data);

export const uploadPhiRecord = (file, meta = {}) => {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('record_type', meta.record_type || 'other');
  if (meta.record_date) fd.append('record_date', meta.record_date);
  if (meta.notes) fd.append('notes', meta.notes);
  if (meta.tags) fd.append('tags', meta.tags);
  if (meta.target_user_id) fd.append('target_user_id', meta.target_user_id);
  return axios.post(`${API}/phi/records`, fd, uploadOpts()).then(r => r.data);
};

export const issuePhiSignedUrl = (id, expiresSeconds = 300) =>
  axios.post(`${API}/phi/records/${id}/signed-url`, { expires_seconds: expiresSeconds }, opts()).then(r => r.data);

export const deletePhiRecord = (id) =>
  axios.delete(`${API}/phi/records/${id}`, opts()).then(r => r.data);

export const adminBulkPhiFiles = (files, ownerIds, recordType = 'other') => {
  const fd = new FormData();
  files.forEach(f => fd.append('files', f));
  fd.append('target_user_ids', JSON.stringify(ownerIds));
  fd.append('record_type', recordType);
  return axios.post(`${API}/phi/records/bulk-files`, fd, uploadOpts()).then(r => r.data);
};

export const adminBulkPhiZip = (zipFile) => {
  const fd = new FormData();
  fd.append('archive', zipFile);
  return axios.post(`${API}/phi/records/bulk-zip`, fd, uploadOpts()).then(r => r.data);
};

export const adminBulkPhiCsv = (csvFile, recordType = 'lab') => {
  const fd = new FormData();
  fd.append('csv_file', csvFile);
  fd.append('record_type', recordType);
  return axios.post(`${API}/phi/records/bulk-csv`, fd, uploadOpts()).then(r => r.data);
};

export const fetchPhiAuditLog = (params = {}) =>
  axios.get(`${API}/phi/audit-log`, { ...opts(), params }).then(r => r.data);

// ── Seminar Video Library ──
export const fetchSeminarVideos = (params = {}) =>
  axios.get(`${API}/seminar-videos`, { ...opts(), params }).then(r => r.data);

export const fetchSeminarVideo = (id) =>
  axios.get(`${API}/seminar-videos/${id}`, opts()).then(r => r.data);

export const uploadSeminarVideo = ({ title, description, category, manual_tags, min_tier, url, thumbnail_url, duration_sec, file }) => {
  const fd = new FormData();
  fd.append('title', title);
  fd.append('description', description || '');
  fd.append('category', category || 'general');
  fd.append('manual_tags', manual_tags || '');
  fd.append('min_tier', String(min_tier ?? 0));
  if (thumbnail_url) fd.append('thumbnail_url', thumbnail_url);
  if (duration_sec) fd.append('duration_sec', String(duration_sec));
  if (url) fd.append('url', url);
  if (file) fd.append('file', file);
  return axios.post(`${API}/seminar-videos`, fd, uploadOpts()).then(r => r.data);
};

export const updateSeminarVideo = (id, patch) =>
  axios.patch(`${API}/seminar-videos/${id}`, patch, opts()).then(r => r.data);

export const deleteSeminarVideo = (id) =>
  axios.delete(`${API}/seminar-videos/${id}`, opts()).then(r => r.data);

export const seminarVideoStreamUrl = (id) => `${API}/seminar-videos/${id}/stream`;
